/**
 * Bridges custom product-page blocks to Horizon's cart.
 *
 * Several blocks on this theme were authored for Dawn: they POST to /cart/add.js, dispatch a
 * `cart:refresh` event, and look for a `<cart-drawer>` element. Horizon uses none of those —
 * it listens for `cart:update` and its element is `<cart-drawer-component>` — so the item
 * landed in the cart but nothing on screen changed until a page reload.
 *
 * This is a classic script, not a module, because the blocks use inline <script> tags and
 * cannot import from the @theme/* importmap.
 *
 * Usage from a block:
 *   await window.GBUCartSync.add([{ id: variantId, quantity: 3 }]);
 */
(function () {
  'use strict';

  /** @returns {string} The section the cart drawer renders inside (Horizon puts it in the header). */
  function getSectionId() {
    var host = document.querySelector('cart-items-component');
    return host && host.dataset && host.dataset.sectionId ? host.dataset.sectionId : '';
  }

  /**
   * Reads the item count out of a rendered section, the way Horizon's own components do.
   * @param {Record<string, string> | undefined} sections
   * @param {string} sectionId
   * @returns {number | null}
   */
  function countFromSections(sections, sectionId) {
    if (!sections || !sectionId || !sections[sectionId]) return null;

    try {
      var parsed = new DOMParser().parseFromString(sections[sectionId], 'text/html');
      var node = parsed.querySelector('[ref="cartItemCount"]');
      if (!node) return null;

      var value = parseInt((node.textContent || '').trim(), 10);
      return isNaN(value) ? null : value;
    } catch (error) {
      return null;
    }
  }

  /**
   * Tells Horizon the cart changed. The detail shape matches what CartUpdateEvent produces,
   * so `cart-items-component` morphs the drawer and `cart-icon` updates the bubble.
   *
   * @param {Record<string, any>} data Response from the cart request.
   * @param {string} sectionId
   */
  async function notify(data, sectionId) {
    var sections = data ? data.sections : undefined;
    var itemCount = countFromSections(sections, sectionId);

    // /cart/add.js returns the new line, not the cart, so without sections there is no count
    // to read. Fetching it is better than reporting 0 and zeroing the bubble.
    if (itemCount === null) {
      try {
        var cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        itemCount = typeof cart.item_count === 'number' ? cart.item_count : 0;
      } catch (error) {
        itemCount = 0;
      }
    }

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: 'gbu-cart-sync',
          data: { source: 'gbu-cart-sync', itemCount: itemCount, sections: sections },
        },
      })
    );
  }

  /** Opens Horizon's cart drawer, if this template has one. */
  function openDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    return false;
  }

  /**
   * Adds line items and brings the drawer in sync.
   * @param {Array<{id: number, quantity: number, properties?: Record<string, any>}>} items
   * @param {{ open?: boolean }} [options]
   */
  async function add(items, options) {
    var sectionId = getSectionId();
    var payload = { items: items };

    // Asking for the section up front means one round trip instead of add-then-refetch.
    if (sectionId) {
      payload.sections = sectionId;
      payload.sections_url = window.location.pathname;
    }

    var response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    var data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Failed to add to cart');
    }

    await notify(data, sectionId);

    if (!options || options.open !== false) openDrawer();

    return data;
  }

  window.GBUCartSync = { add: add, notify: notify, openDrawer: openDrawer };
})();
